import { LENS_LAYOUT } from '~/configs/unifiedAnalyticsTheme';

import { BRAND_COLORS, lensMarkerSymbol } from '~/components/brand';

import type {
  LensCalendar,
  LensCardConfig,
  LensCardType,
  LensLabelFormat,
  LensMark,
  LensTextScale,
  LensValueFormat,
} from '~/types/leLens';

import { AXIS_INTERVALS, type AxisBound, boundFor, truncatesZero } from './lensAxisBounds';
import { LENS_DEFAULT_CALENDAR } from './lensCalendar';
import {
  type LensChartModel,
  type LensMarkEmphasis,
  MAX_RANKED_CATEGORIES,
  displayLensFieldName,
  fieldFormat,
  formatLensLabel,
  formatLensValue,
  inferComboAxes,
  isDateLikeText,
  markEmphasis,
  rankedCategoryRows,
  shouldShowLegend,
} from './lensChart';
import { parseGrain } from './lensGrain';
import { type LensDistribution, type LensMatrixModel } from './lensMatrix';
import {
  type TimeScale,
  instantOf,
  pointLabel,
  sequenceLabels,
  tickUnit,
  timeScaleOf,
  timeTicks,
  truncate,
} from './lensTime';

/**
 * Structural option shape. ECharts is not a dependency of this module: the
 * builder returns a plain object so it can be unit tested, rendered in the
 * browser, or handed to a headless renderer on the server without importing
 * the library here.
 */
export type LensEChartsOption = Record<string, unknown>;

export type LensEChartsTheme = {
  isDark: boolean;
  /** Series palette in slot order, already resolved for the mode. */
  palette: ReadonlyArray<string>;
  text: string;
  textMuted: string;
  grid: string;
  axis: string;
  surface: string;
  fontFamily: string;
  /** The one highlight colour for a called-out series or point. Optional so a
   *  caller built before this existed still resolves to the brand's Coral
   *  Green rather than losing the highlight treatment outright. */
  highlight?: string;
};

export type LensEChartsInput = {
  model: LensChartModel;
  type: LensCardType;
  config?: LensCardConfig;
  labelFormat?: LensLabelFormat;
  theme: LensEChartsTheme;
  /** Adds wheel and slider zoom over the x axis. Only useful past a few dozen marks. */
  zoom?: boolean;
  /** The actual pixel height of the card body this option is drawn into.
   *  Needed only when zoom is on, to pin the slider's vertical position: see
   *  the comment beside `sliderTop` below for why `bottom` alone is not
   *  enough. */
  bodyHeight?: number;
  /** Measured plot width, used to keep dense category labels readable in a narrow card. */
  bodyWidth?: number;
  /** From prepareLensTimeAxis. When isTimeAxis, x becomes a real time scale. */
  timeAxis?: { isTimeAxis: boolean; rows: Array<Record<string, unknown>>; xKey: string; scale?: TimeScale } | null;
  calendar?: LensCalendar;
  /** Index of a trailing still-accumulating period, or -1. Draws it dashed. */
  partialIndex?: number;
  /** Show a series-name label at the last point instead of a legend row. */
  directLabels?: boolean;
  interactiveSeries?: boolean;
  /** An active cross-filter selection on this chart's own grouped column, so
   *  a bar or stacked mark can be highlighted rather than narrowed away.
   *  Null or omitted draws every mark exactly as an unfiltered chart would. */
  crossFilter?: LensEChartsCrossFilter | null;
};

export type LensEChartsCrossFilter = {
  /** The column a click on this chart's own marks would set. */
  column: string;
  /** Currently-selected values for that column. */
  values: ReadonlyArray<string>;
  /** True when `column` names the series (legend), false when it names the x axis. */
  bySeries: boolean;
};

// The Recharts renderer's sx block forced every axis tick and legend label to
// 11px in CSS, overriding the 12px it also passed as component props: 11 is
// what actually rendered, so it is the size to match here too, and it is what
// the medium scale means.
const TEXT_SCALE_PX: Record<LensTextScale, number> = { small: 10, medium: 11, large: 13 };
const TIME_TICK_SPACING = 84;

/**
 * Axis, legend and value-label text size.
 *
 * One number for all of them, because an axis in one size beside a legend in
 * another reads as a mistake rather than a hierarchy. An omitted scale is the
 * 11px the cards already draw, so a card saved before this existed is unchanged.
 */
export function textSizeFor(scale: LensTextScale | undefined): number {
  return TEXT_SCALE_PX[scale ?? 'medium'];
}

/**
 * The medium size, for the builders that do not read a scale yet: pie, scatter,
 * heatmap and boxplot. The control is offered only on the axis charts, so a
 * scale cannot be set on a card these draw.
 */
const AXIS_FONT_SIZE = TEXT_SCALE_PX.medium;
const LEGEND_FONT_SIZE = TEXT_SCALE_PX.medium;

/** Room for an axis title beside the axis labels it names. */
const AXIS_TITLE_WIDTH = 18;
/**
 * The most printed values one series can carry before they overlap. A card body
 * is rarely wider than 900px and a formatted number with its gutter needs about
 * 56px, so past this the labels touch regardless of what the numbers are.
 */
const MAX_VALUE_LABELS = 16;

/**
 * The widest a category label is drawn before it is truncated with an ellipsis.
 * Rotated labels get more room because they run diagonally across the gutter
 * the grid already reserves for them.
 */
const CATEGORY_LABEL_WIDTH = 96;
/** A ranked category label gets two short lines before it yields to an ellipsis.
 *  Ten bars fit comfortably on an ordinary card when each line stays around
 *  this length; longer single-line labels are what turn the axis into a smear. */
const CATEGORY_LABEL_CHARACTERS_PER_LINE = 12;

function wrapCategoryLabel(value: unknown): string {
  const label = String(value ?? '').trim();
  if (label.length <= CATEGORY_LABEL_CHARACTERS_PER_LINE) {
    return label;
  }

  const words = label.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return `${label.slice(0, CATEGORY_LABEL_CHARACTERS_PER_LINE - 1).trimEnd()}…`;
  }

  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= CATEGORY_LABEL_CHARACTERS_PER_LINE || !current) {
      current = candidate;
      continue;
    }
    lines.push(current);
    current = word;
  }
  if (current) {
    lines.push(current);
  }

  if (lines.length <= 2) {
    return lines.join('\n');
  }
  const remainder = lines.slice(1).join(' ');
  const second =
    remainder.length <= CATEGORY_LABEL_CHARACTERS_PER_LINE
      ? remainder
      : `${remainder.slice(0, CATEGORY_LABEL_CHARACTERS_PER_LINE - 1).trimEnd()}…`;
  return `${lines[0]}\n${second}`;
}

/** Room above the plot for the caption naming a truncated axis. */
const TRUNCATION_CAPTION_HEIGHT = 14;

/**
 * The caption a card carries when its axis does not start at zero.
 *
 * A truncated axis is the one setting that changes what a chart appears to say
 * without changing a number in it, so the author owns the floor and the reader
 * is told about it. There is no config key to switch this off.
 *
 * It is a canvas graphic rather than a caption in the DOM around the chart,
 * because the exported picture is the case that matters: a card on a slide has
 * left its dashboard behind, and a caption outside the canvas would not travel
 * with it.
 */
function truncationCaption(
  primary: AxisBound,
  secondary: AxisBound,
  primaryFormat: LensValueFormat | undefined,
  secondaryFormat: LensValueFormat | undefined,
  hasSecondary: boolean,
): string | undefined {
  const primaryCut = truncatesZero(primary);
  const secondaryCut = hasSecondary && truncatesZero(secondary);
  if (!primaryCut && !secondaryCut) {
    return undefined;
  }

  // formatLensValue, not the compact axis label: the caption names the exact
  // floor its author typed, and "$4K" is not that number.
  const edge = (bound: AxisBound, format: LensValueFormat | undefined): string =>
    bound.min !== undefined && bound.min > 0
      ? `starts at ${formatLensValue(bound.min, format)}`
      : `ends at ${formatLensValue(bound.max, format)}`;

  if (primaryCut && secondaryCut) {
    return `Left axis ${edge(primary, primaryFormat)}, right axis ${edge(secondary, secondaryFormat)}, not zero`;
  }
  if (secondaryCut) {
    return `Right axis ${edge(secondary, secondaryFormat)}, not zero`;
  }
  return `Axis ${edge(primary, primaryFormat)}, not zero`;
}
/** Matches the fixed 64px Recharts YAxis width so the plot areas line up. */
const Y_AXIS_WIDTH = 64;
/** Outer breathing room; the renderer adds the actual axis-label/title extent. */
const AXIS_OUTER_INSET = 8;
/** Category names can use up to this share of a horizontal card, while retaining a useful plot. */
const CATEGORY_LABEL_SHARE = 0.45;
const MIN_HORIZONTAL_PLOT_WIDTH = 120;
const HORIZONTAL_PLOT_RIGHT = 36;
const AXIS_LABEL_GAP = 8;
const ZOOM_SLIDER_HEIGHT = 24;
/** Room for the caption above the slider, so it does not land on the axis labels. */
const ZOOM_CAPTION_HEIGHT = 14;
/**
 * Clearance under the slider.
 *
 * Without it the slider's bottom edge lands exactly on `bodyHeight`, the body's own last pixel,
 * because `zoomSliderBottom` is 0 whenever there is no legend. ECharts draws the slider's border
 * and its drag handles fractionally outside the height it was given, and the card clips its
 * overflow, so the control arrives with its bottom shaved off and reads as broken.
 */
const ZOOM_SLIDER_GUTTER = 6;
/** Space between the top of the slider and the caption naming it. */
/** Recharts drew no dot per point and an activeDot radius of 4, i.e. an 8px hover-only marker. */
const HOVER_DOT_SIZE = 8;
/** The white ring Recharts' default activeDot draws around the hover marker, unconditional on theme. */
const HOVER_DOT_BORDER = '#fff';
/** Builders for pie, scatter, heatmap and box plot own their different axis semantics. */
const REFERENCE_LINE_CARD_TYPES: ReadonlyArray<LensCardType> = ['bar', 'stacked', 'line', 'area', 'combo'];
/** Opacity for a bar mark whose value was not part of an active cross-filter
 *  selection on its own column. Not near-invisible: a de-emphasised mark
 *  still answers "was this here", just quietly. */
const DIMMED_BAR_OPACITY = 0.32;
/** Outline width for the mark a cross-filter selection is actually on. Colour
 *  alone would not survive greyscale or a colour-blind palette, so a selected
 *  mark also gets a border in the theme's text colour rather than its series
 *  hue, which is the one colour guaranteed to read against every series. */
const SELECTED_BAR_BORDER_WIDTH = 2;
/** Symbol size for the Lens ring-dot marker on a highlighted line or area
 *  point, per lensMarkerSymbol's own guidance (sized for roughly 20 to 24px). */
const HIGHLIGHT_MARKER_SIZE = 22;
/** Size of both partial-day markers: the last-complete-day point and the
 *  still-accumulating dot beside it. Small and equal, so the fill and border
 *  are the only thing telling a finished point from a provisional one. */
const PARTIAL_MARKER_SIZE = 6;

/**
 * True when a series should draw in the brand's single highlight colour with
 * the ring-dot marker, instead of its ordinary palette slot: either the card
 * author named it explicitly, or an active cross-filter selection on the
 * series column picked it out. A name that matches neither draws exactly as
 * before, and at most one thing on a card is ever highlighted this way.
 */
function isHighlightedSeries(
  name: string,
  config: LensCardConfig | undefined,
  crossFilter: LensEChartsCrossFilter | null,
): boolean {
  if (config?.highlightSeries === name) {
    return true;
  }
  return Boolean(crossFilter?.bySeries && crossFilter.values.includes(name));
}

function compactAxisLabel(format: LensValueFormat | undefined, value: unknown): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '';
  }
  if (format?.kind === 'percent') {
    return formatLensValue(numeric, format);
  }
  const options: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1 };
  if (format?.kind === 'currency' && format.currency) {
    options.style = 'currency';
    options.currency = format.currency;
  }
  return new Intl.NumberFormat(format?.locale || 'en-AU', options).format(numeric);
}

/** Axis ticks need units, but not the fixed decimal padding used by exact tooltips. */
function horizontalAxisLabel(format: LensValueFormat | undefined, value: unknown): string {
  const numeric = Number(value);
  if (format?.kind !== 'percent' || !Number.isFinite(numeric)) {
    return compactAxisLabel(format, value);
  }
  const scaled = (format.percentScale ?? 'percent') === 'percent' ? numeric / 100 : numeric;
  const body = new Intl.NumberFormat(format.locale || 'en-AU', {
    style: 'percent',
    maximumFractionDigits: format.decimals ?? 3,
  }).format(scaled);
  return `${format.prefix ?? ''}${body}${format.suffix ? ` ${format.suffix}` : ''}`;
}

/** Use the actual font in browsers; keep the option builder usable without a canvas. */
function axisTextMeasure(fontSize: number, fontFamily: string): (text: string) => number {
  const context =
    typeof document !== 'undefined' && typeof CanvasRenderingContext2D !== 'undefined'
      ? document.createElement('canvas').getContext('2d')
      : null;
  if (context) {
    context.font = `${fontSize}px ${fontFamily}`;
  }
  return (text) => context?.measureText(text).width ?? text.length * fontSize * AVG_GLYPH_WIDTH;
}

/**
 * Past this the full number stops being a label and becomes a wall of digits.
 *
 * Under it the exact figure is what someone reads a printed value for: 4,120 is
 * the point of putting the number on the bar, and $4.1K is not. Over it the
 * digits no longer fit between marks, so the compact form the axis already uses
 * is the readable one.
 */
const VALUE_LABEL_COMPACT_ABOVE = 10_000;

function valueLabelText(format: LensValueFormat | undefined, value: unknown): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '';
  }
  return Math.abs(numeric) < VALUE_LABEL_COMPACT_ABOVE
    ? formatLensValue(numeric, format)
    : compactAxisLabel(format, numeric);
}

function seriesLabel(column: string, config?: LensCardConfig): string {
  return displayLensFieldName(column, config);
}

/**
 * Fixed guides belong to an axis, not to every series drawn against it.
 *
 * ECharts owns a mark line through a series, so attaching the same guide to
 * every series would make its dash and label draw on top of themselves.  The
 * option builder instead attaches all primary guides to the first primary
 * series and all secondary guides to the first secondary series.  A series is
 * already tied to its own `yAxisIndex`, so ECharts reads each guide from the
 * intended scale without any renderer-specific coordinate calculation.
 */
function referenceLineOption(
  lines: LensCardConfig['referenceLines'] | undefined,
  axis: 'primary' | 'secondary',
  format: LensValueFormat | undefined,
  theme: LensEChartsTheme,
  horizontal = false,
): Record<string, unknown> | undefined {
  const data = (lines ?? [])
    .filter((line) => line && Number.isFinite(line.value) && (line.axis ?? 'primary') === axis)
    .map((line) => {
      const value = formatLensValue(line.value, format);
      const label = line.label?.trim();
      const text = label ? `${label} · ${value}` : value;
      return {
        [horizontal ? 'xAxis' : 'yAxis']: line.value,
        name: text,
        label: {
          show: true,
          formatter: text,
          position: 'insideEndTop',
          color: theme.textMuted,
          fontFamily: theme.fontFamily,
          fontSize: 10,
          backgroundColor: theme.surface,
          padding: [2, 3],
          borderRadius: 2,
        },
      };
    });

  if (!data.length) {
    return undefined;
  }

  return {
    silent: true,
    symbol: 'none',
    lineStyle: { color: theme.axis, type: [4, 4], width: 1 },
    data,
  };
}

/**
 * Widest-case glyph width for the theme's sans-serif stack, as a fraction of font size.
 *
 * Sized for capitals on purpose. A series label here is routinely a market code or a business
 * acronym, and this branch renders both all-caps, so uppercase is the common case rather than the
 * edge. A lowercase average of 0.6 measured "AU" at 13px when it draws nearer 16, which is what
 * clipped every market label on a line card to its first character and a half.
 */
const AVG_GLYPH_WIDTH = 0.72;
/** Gap between a line's last point and its endLabel. Set on the label as well as reserved here,
 *  so the reservation and the render cannot disagree about it. */
const END_LABEL_GAP = 28;
const INTERACTIVE_END_LABEL_PADDING = 8;
const MIN_DIRECT_LABEL_PLOT_WIDTH = 240;
const DIRECT_LABEL_WIDTH_FRACTION = 0.32;
const MAX_VERTICAL_SERIES_LEGEND = 4;
const MAX_LEGEND_HEIGHT_FRACTION = 0.4;

/**
 * There is no canvas to measure text with here, so the longest label's width
 * is estimated from its character count. Overshooting costs a little empty
 * margin; undershooting clips the label, which is the defect this exists to
 * avoid.
 */
function estimateEndLabelWidth(labels: ReadonlyArray<string>, fontSize: number): number {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return longest === 0 ? 0 : Math.ceil(longest * fontSize * AVG_GLYPH_WIDTH) + END_LABEL_GAP;
}

/** Wrap a narrow legend without discarding any part of the series name. */
function wrapSeriesLabel(label: string, maxCharacters: number): string {
  const lines: Array<string> = [];
  let line = '';
  for (const word of label.split(/\s+/)) {
    if (line && line.length + word.length + 1 > maxCharacters) {
      lines.push(line);
      line = '';
    }
    let remainder = word;
    while (remainder.length > maxCharacters) {
      lines.push(remainder.slice(0, maxCharacters));
      remainder = remainder.slice(maxCharacters);
    }
    line = line ? `${line} ${remainder}` : remainder;
  }
  if (line) {
    lines.push(line);
  }
  return lines.join('\n');
}

function colourFor(
  model: LensChartModel,
  theme: LensEChartsTheme,
  name: string,
  index: number,
  highlighted: boolean,
): string {
  if (highlighted) {
    return theme.highlight ?? BRAND_COLORS.coralGreen;
  }
  const slot = model.colorIndex[name] ?? index;
  return theme.palette[slot % theme.palette.length];
}

/**
 * Whether an x column reads as a date, checked across every row rather than
 * one value: a bar or stacked card's x axis is category, never time (see
 * prepareLensTimeAxis in lensChart), so this is the only signal a category
 * label has that it should render as a date instead of the raw text it
 * arrived as.
 */
function isDateLikeCategoryColumn(rows: Array<Record<string, unknown>>, xKey: string): boolean {
  const values = rows.map((row) => row[xKey]).filter((value) => value !== null && value !== undefined && value !== '');
  return values.length > 0 && values.every((value) => isDateLikeText(value));
}

/**
 * Bars carry a rounded cap only when they are the top of their column. A
 * rounded segment mid-stack reads as a gap between two adjacent values.
 *
 * `emphasis` layers a cross-filter's highlight on top of that base look:
 * 'dimmed' fades a mark that was not part of the click, and 'selected' adds a
 * visible outline to the one that was, so the distinction survives greyscale
 * and colour blindness rather than resting on colour or opacity alone.
 */
function barItemStyle(
  colour: string,
  stacked: boolean,
  surface: string,
  emphasis: LensMarkEmphasis = 'normal',
  outline?: string,
  /** Fill for the exact mark a point-level (x axis) selection is on. A
   *  series-level highlight already reaches this function through `colour`
   *  itself, so this only matters for the per-point case. */
  highlightFill?: string,
) {
  const base = stacked
    ? { color: colour, borderColor: surface, borderWidth: 2 }
    : { color: colour, borderRadius: [4, 4, 0, 0] };
  if (emphasis === 'dimmed') {
    return { ...base, opacity: DIMMED_BAR_OPACITY };
  }
  if (emphasis === 'selected') {
    return {
      ...base,
      ...(highlightFill ? { color: highlightFill } : {}),
      ...(outline ? { borderColor: outline, borderWidth: SELECTED_BAR_BORDER_WIDTH } : {}),
    };
  }
  return base;
}

/** The plotted number, whether the series carries a scalar or an [x, y] pair. */
function pointValue(value: unknown): unknown {
  return Array.isArray(value) ? value[value.length - 1] : value;
}

export function buildLensEChartsOption({
  model,
  type,
  config,
  labelFormat,
  theme,
  zoom = false,
  bodyHeight,
  bodyWidth,
  timeAxis = null,
  partialIndex = -1,
  directLabels = false,
  interactiveSeries = false,
  crossFilter = null,
  calendar = LENS_DEFAULT_CALENDAR,
}: LensEChartsInput): LensEChartsOption {
  const stacked = type === 'stacked' || Boolean(config?.stacked);
  const combo = type === 'combo' ? inferComboAxes(model, config) : null;
  const secondary = combo?.secondary ?? [];
  const rotation = config?.xAxis?.tickRotation ?? 0;
  const allTicks = config?.xAxis?.tickDensity === 'all';
  const fontSize = textSizeFor(config?.textScale);
  // Trimmed to undefined so a title saved as whitespace draws nothing and
  // reserves no room for itself.
  const yTitle = config?.yAxis?.title?.trim() || undefined;
  const y2Title = config?.secondaryYAxis?.title?.trim() || undefined;
  const xTitle = config?.xAxis?.title?.trim() || undefined;
  const axisTitleStyle = { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize };
  /**
   * Which series print their own value.
   *
   * An omitted series list means every series, so turning labels on is one
   * switch for the common case and the list is the exception.
   */
  const valueLabelMode = config?.valueLabels?.mode ?? 'values';
  const selectedForLabels = (name: string): boolean =>
    !config?.valueLabels?.series?.length || config.valueLabels.series.includes(name);
  const totalLabelSeries = [...model.series].reverse().find(selectedForLabels);
  const labelled = (name: string): boolean =>
    Boolean(config?.valueLabels?.show) &&
    selectedForLabels(name) &&
    (valueLabelMode !== 'totals' || name === totalLabelSeries);
  const anyLabelled = model.series.some(labelled);
  const isTimeAxis = Boolean(timeAxis?.isTimeAxis);
  const dateCategories =
    !isTimeAxis && !labelFormat && isDateLikeCategoryColumn(model.rows, model.xKey)
      ? timeScaleOf(
          model.rows.map((row) => row[model.xKey]),
          calendar,
          model.xKey,
          parseGrain(model.xKey)?.grain,
        )
      : undefined;
  const isCategoricalBar = !isTimeAxis && (type === 'bar' || type === 'stacked');
  const rankedRows = isCategoricalBar ? rankedCategoryRows(model, config) : model.rows;
  const categoryLimitApplied = isCategoricalBar && rankedRows.length < model.rows.length;
  const categoricalRows = categoryLimitApplied ? rankedRows : model.rows;
  // A ranked top-ten comparison already fits in full. Keeping the source
  // result's zoom control would imply that omitted categories are still
  // available on the axis and would waste a large band beneath the plot.
  const chartZoom = zoom && !categoryLimitApplied;
  // Ten vendor names cannot sit under ten narrow vertical bars in a half-width card. Keep
  // every category visible by using horizontal bars there, while respecting any saved choice.
  const narrowDenseBars =
    type === 'bar' &&
    !isTimeAxis &&
    !dateCategories &&
    bodyWidth !== undefined &&
    bodyWidth <= 720 &&
    rankedRows.length >= 8 &&
    config?.barOrientation === undefined;
  const horizontal =
    !isTimeAxis &&
    (type === 'bar' || type === 'stacked') &&
    (config?.barOrientation === 'horizontal' || narrowDenseBars) &&
    !secondary.length;
  // A time axis reads the rows prepareLensTimeAxis produced, which carry the
  // same series values as model.rows plus the epoch field; a category axis
  // uses the ranked comparison set above.
  const rows = isTimeAxis ? timeAxis.rows : categoricalRows;
  // How many marks each series draws, and how many of them can carry a printed
  // value without the run turning into a band. A card body is rarely wider than
  // 900px and a formatted number with its gutter takes about 56px of it, so
  // past sixteen labels they are touching whatever the numbers are.
  const markCount = rows.length;
  const labelFontSize = textSizeFor(config?.valueLabels?.size ?? config?.textScale);
  const totalsByIndex = rows.map((row) =>
    model.series.reduce((total, seriesName) => {
      const value = Number(row[seriesName]);
      return Number.isFinite(value) ? total + value : total;
    }, 0),
  );
  const topLabelIndexes = new Set(
    totalsByIndex
      .map((value, index) => ({ index, value: Math.abs(value) }))
      .sort((left, right) => right.value - left.value)
      .slice(0, 3)
      .map(({ index }) => index),
  );
  const labelStride = markCount > MAX_VALUE_LABELS ? Math.ceil(markCount / MAX_VALUE_LABELS) : 1;
  const timeValues = isTimeAxis ? rows.map((row) => Number(row[timeAxis.xKey])) : null;
  const toSeriesData = (values: Array<number | null>): unknown =>
    isTimeAxis ? timeValues.map((x, i) => [x, values[i]]) : values;
  const scale = isTimeAxis ? timeAxis.scale : undefined;
  const timeMin = timeValues?.length ? Math.min(...timeValues) : 0;
  const timeMax = timeValues?.length ? Math.max(...timeValues) : 0;
  const ticks = scale
    ? timeTicks(timeMin, timeMax, scale, Math.max(2, Math.floor((bodyWidth ?? 480) / TIME_TICK_SPACING)))
    : [];
  const tickLabels = scale
    ? new Map(
        sequenceLabels(ticks, { ...scale, unit: tickUnit(ticks, scale) }).map((label, index) => [ticks[index], label]),
      )
    : new Map<number, string>();
  const timeLabel = (value: unknown): string =>
    labelFormat
      ? formatLensLabel(new Date(Number(value)), labelFormat, scale?.calendar)
      : scale
        ? pointLabel(Number(value), scale)
        : String(value);
  // A time axis positions each point by its value, so reversing the rows moves
  // nothing on screen. Sorting a date axis descending only means anything if
  // the axis itself is inverted.
  const descendingTime =
    isTimeAxis &&
    Array.isArray(timeValues) &&
    timeValues.length > 1 &&
    Number(timeValues[0]) > Number(timeValues[timeValues.length - 1]);

  // A category axis never becomes the time axis above, so a date-like x
  // column still arrives here as raw query text. An explicit labelFormat is
  // the card author's own choice and always wins over this substitution.
  const isDateCategoryAxis = Boolean(dateCategories);
  const dateCategoryLabels = dateCategories
    ? sequenceLabels(
        rows.map((row) => instantOf(row[model.xKey], calendar, model.xKey) ?? 0),
        dateCategories.scale,
      )
    : undefined;
  // Ranked categories name every bar; a temporal bar chart keeps every day
  // but lets ECharts thin colliding tick labels. Showing every date label
  // turns a month into an unreadable smear even though the marks themselves
  // still have enough room.
  const showEveryCategoryTick = (isCategoricalBar && !isDateCategoryAxis) || allTicks;
  const wrapCategoryLabels = showEveryCategoryTick && !isDateCategoryAxis && rotation === 0 && !horizontal;
  const categories = rows.map((row, index) =>
    dateCategoryLabels ? dateCategoryLabels[index] : formatLensLabel(row[model.xKey], labelFormat),
  );

  const sharedFormat = config?.format;
  const primaryFormat = combo ? combo.primaryFormat : sharedFormat;

  const primarySeries = model.series.filter((name) => !secondary.includes(name));
  const primaryBound: AxisBound = boundFor(model, primarySeries, stacked, config?.yAxis);
  const secondaryBound: AxisBound = boundFor(model, secondary, false, config?.secondaryYAxis);

  const supportsReferenceLines = REFERENCE_LINE_CARD_TYPES.includes(type);
  const primaryReferenceLines = supportsReferenceLines
    ? referenceLineOption(config?.referenceLines, 'primary', primaryFormat, theme, horizontal)
    : undefined;
  const secondaryReferenceLines = supportsReferenceLines
    ? referenceLineOption(config?.referenceLines, 'secondary', combo?.secondaryFormat, theme)
    : undefined;
  const primaryReferenceSeries = primarySeries[0];
  const secondaryReferenceSeries = secondary[0];

  const truncationNote = truncationCaption(
    primaryBound,
    secondaryBound,
    primaryFormat,
    combo?.secondaryFormat,
    secondary.length > 0,
  );

  // Outside a combo the card type is the only thing that says what to draw:
  // without this every line and area card renders as bars.
  const defaultMark: LensMark = type === 'line' ? 'line' : type === 'area' ? 'area' : 'bar';

  // A direct label draws past its series' last point, outside the plot area
  // the grid otherwise reserves: without extra room on the right the canvas
  // edge clips it mid-word. Bars never carry one, so they do not need it.
  const endLabelNames = directLabels
    ? model.series.filter((name) => {
        const mark = combo ? combo.marks[name] : (config?.seriesMarks?.[name] ?? defaultMark);
        return mark === 'line' || mark === 'area';
      })
    : [];
  const estimatedEndLabelWidth = estimateEndLabelWidth(
    endLabelNames.map((name) => seriesLabel(name, config)),
    fontSize,
  );
  const fullEndLabelWidth =
    interactiveSeries && estimatedEndLabelWidth
      ? estimatedEndLabelWidth + INTERACTIVE_END_LABEL_PADDING * 2
      : estimatedEndLabelWidth;
  const availableWidth = bodyWidth ?? 480;
  const directLabelBudget = Math.max(
    0,
    Math.min(availableWidth * DIRECT_LABEL_WIDTH_FRACTION, availableWidth - MIN_DIRECT_LABEL_PLOT_WIDTH),
  );
  const useDirectLabels = directLabels && fullEndLabelWidth <= directLabelBudget;
  const responsiveLegend = directLabels && !useDirectLabels;
  const showLegend = !useDirectLabels && (responsiveLegend || shouldShowLegend(model.series.length));
  const endLabelWidth = useDirectLabels ? fullEndLabelWidth : 0;
  // Partial overlays never grow a second, identical-looking legend row.
  const legendNames = model.series.map((name) => seriesLabel(name, config));
  const legendCharacters = Math.max(4, Math.floor((availableWidth - 96) / (fontSize * AVG_GLYPH_WIDTH)));
  const legendLabel = (name: string) => (responsiveLegend ? wrapSeriesLabel(name, legendCharacters) : name);
  const legendLines = legendNames.map((name) => legendLabel(name).split('\n').length);
  const verticalLegendHeight =
    legendLines.reduce((sum, count) => sum + count, 0) * (fontSize + 4) + Math.max(0, legendNames.length - 1) * 8 + 12;
  const verticalLegend =
    responsiveLegend &&
    model.series.length <= MAX_VERTICAL_SERIES_LEGEND &&
    verticalLegendHeight <= (bodyHeight ?? 280) * MAX_LEGEND_HEIGHT_FRACTION;
  const legendInset = showLegend
    ? responsiveLegend
      ? verticalLegend
        ? verticalLegendHeight
        : Math.max(...legendLines) * (fontSize + 4) + 12
      : 28
    : 8;

  // Only the trailing row can be a genuine partial period, and a stacked
  // overlay would double-count the shared point it sits on top of.
  const hasPartialOverlay = partialIndex === rows.length - 1 && rows.length > 1 && !stacked;

  // Tooltip entries are matched back to a column by seriesIndex against this,
  // not model.series directly: a partial overlay inserts a second entry for a
  // series, which would otherwise shift every later series' index out of step.
  const seriesColumns: Array<string> = [];

  // A cross-filter on this chart's own series column reads at the series
  // level: the whole legend entry is either the one that was clicked or not.
  // A cross-filter on the x axis instead reads per point, since every series
  // shares the same categories, so it is computed once per row below rather
  // than once per series.
  const seriesCrossFilterEmphasis = (name: string): LensMarkEmphasis => {
    if (!crossFilter || !crossFilter.bySeries || !crossFilter.values.length) {
      return 'normal';
    }
    return markEmphasis(name, crossFilter.values);
  };
  const rowCrossFilterEmphasis: Array<LensMarkEmphasis> | null =
    crossFilter && !crossFilter.bySeries && crossFilter.values.length
      ? rows.map((row) => markEmphasis(String(row[model.xKey] ?? ''), crossFilter.values))
      : null;

  const series = model.series.flatMap((name, index) => {
    const highlighted = isHighlightedSeries(name, config, crossFilter);
    const colour = colourFor(model, theme, name, index, highlighted);
    const mark = combo ? combo.marks[name] : (config?.seriesMarks?.[name] ?? defaultMark);
    const onSecondary = secondary.includes(name);
    const drawsPartial = hasPartialOverlay && (mark === 'line' || mark === 'area');
    const label = seriesLabel(name, config);
    // Recharts drew every area mark, and every mark in a combo, on an
    // unconditional monotone curve; only a plain line card's curve followed
    // the line-style setting.
    const smooth = mark === 'area' || Boolean(combo) ? true : config?.lineStyle === 'smooth';

    const ys = rows.map((row) => {
      const value = row[name];
      const numeric = typeof value === 'number' ? value : value === null || value === undefined ? NaN : Number(value);
      // A gap, not a point. isMeasureColumn already rejects a column holding
      // anything unparseable, so this is a guard rather than a live path, but a
      // single infinity reaching the data would take the whole axis scale with
      // it and the cost of not finding out is higher than the cost of the check.
      return Number.isFinite(numeric) ? numeric : null;
    });
    // The primary line stops one point short so the dashed twin below is the
    // only mark drawn across the still-accumulating segment.
    const primaryYs = drawsPartial ? ys.map((y, i) => (i === ys.length - 1 ? null : y)) : ys;

    // A label on the primary would land on its now-null last point once a
    // partial overlay is in play; the twin carries the true final point instead.
    const endLabel =
      useDirectLabels && (mark === 'line' || mark === 'area')
        ? {
            show: true,
            formatter: label,
            color: colour,
            fontSize,
            fontFamily: theme.fontFamily,
            distance: END_LABEL_GAP,
            ...(interactiveSeries
              ? {
                  lineHeight: 24,
                  padding: [0, INTERACTIVE_END_LABEL_PADDING],
                }
              : {}),
          }
        : undefined;

    const base = {
      id: `lens-series:${name}`,
      triggerLineEvent: interactiveSeries,
      name: label,
      data: toSeriesData(primaryYs),
      yAxisIndex: onSecondary ? 1 : 0,
      animation: false,
      // A fixed reference guide is attached to one series on its axis only.
      // The partial overlay below deliberately does not inherit it: it is not a
      // distinct series, just the last dashed segment of this one.
      ...(!onSecondary && name === primaryReferenceSeries && primaryReferenceLines
        ? { markLine: primaryReferenceLines }
        : onSecondary && name === secondaryReferenceSeries && secondaryReferenceLines
          ? { markLine: secondaryReferenceLines }
          : {}),
    };

    // A stacked segment has no room above it, so its own value prints inside it
    // in the surface colour; every other mark prints above itself in the text
    // colour. hideOverlap resolves label-against-label collisions only, so a
    // number wider than a thin stacked segment still overhangs it.
    const valueFormat = fieldFormat(config, name) ?? sharedFormat;
    const valueLabel = labelled(name)
      ? {
          label: {
            show: true,
            position:
              stacked && valueLabelMode === 'values'
                ? ('inside' as const)
                : horizontal
                  ? ('right' as const)
                  : ('top' as const),
            formatter: (point: { value?: unknown; dataIndex?: number }) => {
              const dataIndex = point.dataIndex;
              if (typeof dataIndex !== 'number') {
                return '';
              }
              if (valueLabelMode === 'last' && dataIndex !== markCount - 1) {
                return '';
              }
              if (valueLabelMode === 'top' && !topLabelIndexes.has(dataIndex)) {
                return '';
              }
              if (valueLabelMode === 'totals') {
                return valueLabelText(valueFormat, totalsByIndex[dataIndex]);
              }
              // Thinned before it is drawn rather than hidden after. hideOverlap
              // resolves a label against its neighbour, so on a dense series it
              // leaves whichever labels happen not to touch, which is an
              // arbitrary subset that changes with the numbers. A stride keeps
              // an even spread and always keeps the last point, so the run
              // reads as a labelled sample of the series rather than a band.
              return valueLabelMode === 'values' &&
                labelStride > 1 &&
                dataIndex % labelStride !== 0 &&
                dataIndex !== markCount - 1
                ? ''
                : valueLabelText(valueFormat, pointValue(point.value));
            },
            color: stacked && valueLabelMode === 'values' ? theme.surface : theme.text,
            fontFamily: theme.fontFamily,
            fontSize: labelFontSize,
          },
          labelLayout: { hideOverlap: true },
        }
      : {};

    seriesColumns.push(name);

    // A highlighted line or area always shows the Lens ring-dot at every
    // point, in place of Recharts' old hover-only circle: the mark exists to
    // call this series out, so it has to read that way at rest, not just on
    // hover. An unhighlighted series keeps the plain hover-only dot.
    const pointSymbol = highlighted
      ? { symbol: lensMarkerSymbol(theme.isDark), symbolSize: HIGHLIGHT_MARKER_SIZE, showSymbol: true }
      : { symbol: 'circle', symbolSize: HOVER_DOT_SIZE, showSymbol: false };

    const primaryEntry =
      mark === 'line'
        ? {
            ...base,
            type: 'line',
            ...pointSymbol,
            smooth,
            lineStyle: { color: colour, width: 2 },
            itemStyle: { color: colour, borderColor: HOVER_DOT_BORDER, borderWidth: 2 },
            // Downsamples on the way to the canvas rather than on the way out of
            // the data layer, so the drawn line keeps its peaks and troughs.
            sampling: 'lttb',
            ...valueLabel,
            ...(endLabel && !drawsPartial ? { endLabel, labelLayout: { moveOverlap: 'shiftY' } } : {}),
          }
        : mark === 'area'
          ? {
              ...base,
              type: 'line',
              ...pointSymbol,
              smooth,
              stack: stacked ? 'stack' : undefined,
              lineStyle: { color: colour, width: 2 },
              itemStyle: { color: colour, borderColor: HOVER_DOT_BORDER, borderWidth: 2 },
              areaStyle: { color: colour, opacity: stacked ? 0.85 : 0.3 },
              sampling: 'lttb',
              ...valueLabel,
              ...(endLabel && !drawsPartial ? { endLabel, labelLayout: { moveOverlap: 'shiftY' } } : {}),
            }
          : {
              ...base,
              // Per-point styling overrides base.data only when the active
              // cross-filter is on this chart's own x axis: every other case
              // (no selection, or a selection on the series column instead)
              // leaves the plain numeric array base already carries.
              ...(rowCrossFilterEmphasis
                ? {
                    data: primaryYs.map((value, i) => ({
                      value: isTimeAxis && timeValues ? [timeValues[i], value] : value,
                      itemStyle: barItemStyle(
                        colour,
                        stacked,
                        theme.surface,
                        rowCrossFilterEmphasis[i],
                        theme.text,
                        theme.highlight ?? BRAND_COLORS.coralGreen,
                      ),
                    })),
                  }
                : {}),
              type: 'bar',
              stack: stacked ? 'stack' : undefined,
              itemStyle: barItemStyle(colour, stacked, theme.surface, seriesCrossFilterEmphasis(name), theme.text),
              ...valueLabel,
              // The large-scale bar path skips per-mark labels, so a card asked
              // to print its values draws them instead of optimising them away.
              large: !anyLabelled,
              largeThreshold: 400,
            };

    if (!drawsPartial) {
      return [primaryEntry];
    }

    // Only the last two points carry a value: every earlier one is null so
    // the dashed segment starts exactly where the solid one stops.
    const twinYs = ys.map((y, i) => (i >= ys.length - 2 ? y : null));
    const boundaryIndex = ys.length - 2;
    const partialPointIndex = ys.length - 1;
    // Both real points get their own marker, styled per-point rather than at
    // the series level, so the one dashed line can carry two different marks:
    // a finished point and a still-accumulating one. Everything before them
    // stays a bare null, which draws nothing.
    const twinData = (toSeriesData(twinYs) as Array<unknown>).map((point, index) => {
      if (index === boundaryIndex) {
        // The last complete day: an ordinary, always-visible marker in the
        // series' own colour, so the finished trend still reads as ending on
        // a real point rather than fading into the dashed guide beside it.
        return {
          value: point,
          symbol: 'circle',
          symbolSize: PARTIAL_MARKER_SIZE,
          itemStyle: { color: colour, borderColor: colour, borderWidth: 0 },
        };
      }
      if (index === partialPointIndex) {
        // The still-accumulating day: a light, hollow dot, never the series'
        // own colour, so a genuinely lower running total never reads as a
        // drop in the finished trend.
        return {
          value: point,
          symbol: 'circle',
          symbolSize: PARTIAL_MARKER_SIZE,
          itemStyle: { color: theme.surface, borderColor: colour, borderWidth: 1.5 },
        };
      }
      return point;
    });
    seriesColumns.push(name);
    const twinEntry = {
      id: `lens-partial:${name}`,
      triggerLineEvent: interactiveSeries,
      name: `${label}__partial`,
      data: twinData,
      yAxisIndex: onSecondary ? 1 : 0,
      animation: false,
      // Not a real series as far as the legend is concerned: it shares its
      // primary's name and exists only to draw the trailing dash and the two
      // markers either side of it.
      legendHoverLink: false,
      type: 'line',
      showSymbol: true,
      smooth,
      // A muted guide, never the series' own data colour: the connector is a
      // structural note ("this point is still accumulating"), not a
      // continuation of the finished trend line, so it must never read as
      // one. [3, 4] keeps the dash visibly finer than the 2px solid line.
      lineStyle: { color: theme.axis, width: 1.5, type: [3, 4] },
      itemStyle: { color: theme.axis },
      ...(valueLabelMode === 'last' ? valueLabel : {}),
      ...(mark === 'area' ? { areaStyle: { color: colour, opacity: 0.15 } } : {}),
      ...(endLabel ? { endLabel, labelLayout: { moveOverlap: 'shiftY' } } : {}),
    };

    return [primaryEntry, twinEntry];
  });

  const yAxis: Array<Record<string, unknown>> = [
    {
      type: 'value',
      splitNumber: AXIS_INTERVALS,
      // Absent leaves ECharts its own floor, which is zero for the data these
      // cards carry. A number here is an author's, and boundFor has already
      // refused it if any mark falls below it.
      min: primaryBound.min,
      max: primaryBound.max,
      interval: primaryBound.interval,
      // A middle-placed name on a left axis is rotated vertically by ECharts,
      // which is the orientation that fits a card's width.
      ...(yTitle
        ? { name: yTitle, nameLocation: 'middle', nameGap: Y_AXIS_WIDTH - 12, nameTextStyle: axisTitleStyle }
        : {}),
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize,
        formatter: (value: unknown) => compactAxisLabel(primaryFormat, value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      // [3, 3] to match Recharts' CartesianGrid strokeDasharray="3 3" exactly.
      splitLine: { lineStyle: { color: theme.grid, type: [3, 3] } },
    },
  ];

  if (secondary.length > 0) {
    yAxis.push({
      type: 'value',
      position: 'right',
      splitNumber: AXIS_INTERVALS,
      min: secondaryBound.min,
      max: secondaryBound.max,
      interval: secondaryBound.interval,
      ...(y2Title
        ? { name: y2Title, nameLocation: 'middle', nameGap: Y_AXIS_WIDTH - 12, nameTextStyle: axisTitleStyle }
        : {}),
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize,
        formatter: (value: unknown) => compactAxisLabel(combo?.secondaryFormat, value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    });
  }

  const rankingGraphic = categoryLimitApplied
    ? {
        type: 'text',
        silent: true,
        left: 0,
        bottom: 0,
        style: {
          text:
            config?.categoryOrder?.length || config?.sort
              ? `First ${MAX_RANKED_CATEGORIES} of ${formatLensValue(model.sourceRowCount)}`
              : `Showing top ${MAX_RANKED_CATEGORIES} of ${formatLensValue(model.sourceRowCount)}`,
          fill: theme.textMuted,
          fontSize: 10,
          fontFamily: theme.fontFamily,
        },
      }
    : null;

  // Shared with the caption below the slider, so the two never drift apart.
  const zoomSliderBottom = showLegend ? legendInset - 4 : 0;
  // The slider's own default option leaves `top: 'ph'`, a placeholder ECharts
  // resolves to a fixed anchor near the whole chart's bottom edge the moment
  // `top` is left unset; once resolved it is a real number, so ECharts' own
  // layout rect stops deriving a position from `bottom` at all (it only falls
  // back to `bottom` when `top` is still unresolved). Measured directly, a
  // `bottom: 0` slider configured that way renders a few pixels past the
  // container's own edge regardless of the grid's bottom reservation above.
  // An explicit pixel `top`, computed from the body height the card was
  // actually given, is the only value ECharts will honour instead.
  // One number for how far the slider's underside sits above the body's bottom edge. The caption
  // is placed from this too: they were computed separately, so adding the gutter moved the slider
  // up and left the caption printing inside the band it is supposed to label.
  const zoomSliderInset = zoomSliderBottom + ZOOM_SLIDER_GUTTER;
  const sliderTop =
    chartZoom && bodyHeight !== undefined ? bodyHeight - zoomSliderInset - ZOOM_SLIDER_HEIGHT : undefined;

  const topInset = 12 + (anyLabelled && !stacked ? fontSize + 4 : 0) + (truncationNote ? TRUNCATION_CAPTION_HEIGHT : 0);
  const footerInset =
    legendInset +
    (xTitle ? AXIS_TITLE_WIDTH : 0) +
    (categoryLimitApplied ? 16 : 0) +
    (chartZoom ? ZOOM_SLIDER_HEIGHT + ZOOM_CAPTION_HEIGHT + 8 + ZOOM_SLIDER_GUTTER : 0);

  const option: LensEChartsOption = {
    animation: false,
    textStyle: { fontFamily: theme.fontFamily, color: theme.text },
    grid: {
      // ECharts 6 measures the actual formatted ticks and axis titles inside
      // this outer frame. A short 3K tick must not inherit a fixed 64px gutter.
      left: AXIS_OUTER_INSET,
      // Direct series labels live outside the plot, unlike axis labels, so
      // their existing separate reservation remains necessary.
      right: Math.max(16, endLabelWidth),
      // A value label prints above its mark, outside the plot area the peak
      // otherwise fills, so a labelled card gives the top row somewhere to go.
      // The truncation caption sits above both.
      top: topInset,
      // Retain the preferred vertical plot budget for existing card layouts.
      // The outer frame leaves this label band available to the renderer, so
      // measured labels do not add the same band a second time.
      bottom: footerInset + (rotation ? 44 : wrapCategoryLabels ? 32 : 16),
      outerBoundsMode: 'auto',
      outerBounds: { left: AXIS_OUTER_INSET, right: Math.max(16, endLabelWidth), top: topInset, bottom: footerInset },
      outerBoundsContain: 'all',
    },
    legend: useDirectLabels
      ? { show: false }
      : showLegend
        ? {
            show: true,
            // A legend wider than the card silently clips its tail, so a
            // reader sees six of eight series and has no way to know. Scrolling
            // keeps every entry reachable, and the page indicator says how many
            // are off screen rather than leaving the row to end mid-name.
            type: verticalLegend ? 'plain' : 'scroll',
            ...(responsiveLegend ? { formatter: legendLabel } : {}),
            ...(verticalLegend ? { orient: 'vertical', left: 'center', itemGap: 8 } : {}),
            data: legendNames,
            bottom: categoryLimitApplied ? 18 : 0,
            icon: 'roundRect',
            itemHeight: 8,
            itemWidth: 12,
            pageIconColor: theme.textMuted,
            pageIconInactiveColor: theme.axis,
            pageIconSize: 10,
            pageTextStyle: { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize },
            pageFormatter: (page: { current: number; total: number }) =>
              page.total > 1 ? `${page.current}/${page.total}` : '',
            textStyle: {
              color: theme.textMuted,
              fontFamily: theme.fontFamily,
              fontSize,
              ...(responsiveLegend ? { lineHeight: fontSize + 4 } : {}),
            },
          }
        : { show: false },
    tooltip: {
      trigger: 'axis',
      // Recharts drew a shaded band for every card except a genuine time
      // axis, which got a dashed vertical line instead; a combo or a
      // category-keyed line/area card never built a time axis, so this
      // follows isTimeAxis rather than the card type.
      axisPointer: isTimeAxis
        ? { type: 'line', lineStyle: { color: theme.textMuted, type: [3, 3] } }
        : { type: 'shadow', shadowStyle: { color: theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' } },
      backgroundColor: theme.surface,
      borderColor: theme.axis,
      textStyle: { color: theme.text, fontFamily: theme.fontFamily, fontSize: 12 },
      formatter: (params: unknown) => {
        // The partial overlay shares its value with the series it extends, so
        // it is dropped here rather than shown as a second, identical row.
        const entries = (Array.isArray(params) ? params : [params]).filter(
          (entry) => !String((entry as { seriesName?: string }).seriesName ?? '').endsWith('__partial'),
        );
        if (!entries.length) {
          return '';
        }
        // On a time axis axisValueLabel is ECharts' own rendering of the
        // instant, which disagrees with the ticks beside it and ignores the
        // card's label format.
        const first = entries[0] as { axisValueLabel?: string; value?: unknown };
        const head = isTimeAxis ? timeLabel((first.value as Array<unknown>)?.[0]) : (first.axisValueLabel ?? '');
        const lines = entries.map((entry) => {
          const row = entry as { marker?: string; seriesName?: string; value?: unknown; seriesIndex?: number };
          const column = seriesColumns[row.seriesIndex ?? 0] ?? '';
          const value = formatLensValue(pointValue(row.value), fieldFormat(config, column) ?? sharedFormat);
          return `${row.marker ?? ''}${row.seriesName ?? ''}: <b>${value}</b>`;
        });
        if (stacked && entries.length > 1) {
          const total = entries.reduce((sum, entry) => {
            const value = Number(pointValue((entry as { value?: unknown }).value));
            return Number.isFinite(value) ? sum + value : sum;
          }, 0);
          lines.push(`Total: <b>${formatLensValue(total, sharedFormat)}</b>`);
        }
        return [head, ...lines].join('<br/>');
      },
    },
    dataZoom: chartZoom
      ? [
          { type: 'inside', filterMode: 'none' },
          {
            type: 'slider',
            height: ZOOM_SLIDER_HEIGHT,
            ...(sliderTop !== undefined ? { top: sliderTop } : { bottom: zoomSliderInset }),
            filterMode: 'none',
            borderColor: theme.axis,
            fillerColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
            handleStyle: { color: theme.surface, borderColor: theme.axis },
            textStyle: { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize: 10 },
            // Without this the handles print ECharts' own stamp, to the second
            // and in UTC, disagreeing with the axis right above them.
            ...(isTimeAxis
              ? {
                  labelFormatter: (value: number) =>
                    timeLabel(scale ? truncate(value, scale.unit, scale.calendar) : value),
                }
              : {}),
          },
        ]
      : undefined,
    // Two canvas captions, when they apply. The slider otherwise looks like a
    // stray bar under the plot with nothing saying it is a range control, and a
    // truncated axis otherwise looks exactly like one drawn from zero. Both in
    // the same muted tone as the axis labels, naming themselves without
    // competing for attention with the chart.
    graphic: (() => {
      const items = [
        ...(truncationNote
          ? [
              {
                type: 'text',
                silent: true,
                // Top left. The partial-day note is DOM at top right, so the two
                // never land on each other.
                left: AXIS_OUTER_INSET,
                top: 0,
                style: {
                  text: truncationNote,
                  fill: theme.textMuted,
                  fontFamily: theme.fontFamily,
                  fontSize: 10,
                  fontStyle: 'italic',
                },
              },
            ]
          : []),
        ...(rankingGraphic ? [rankingGraphic] : []),
      ];
      return items.length ? items : undefined;
    })(),
    xAxis: isTimeAxis
      ? {
          type: 'time',
          min: timeMin,
          max: timeMax,
          inverse: descendingTime,
          ...(xTitle
            ? {
                name: xTitle,
                nameLocation: 'middle',
                nameGap: rotation ? 56 : wrapCategoryLabels ? 44 : 28,
                nameTextStyle: axisTitleStyle,
              }
            : {}),
          axisLabel: {
            color: theme.textMuted,
            fontFamily: theme.fontFamily,
            fontSize,
            customValues: ticks,
            formatter: (value: unknown) =>
              labelFormat ? timeLabel(value) : (tickLabels.get(Number(value)) ?? timeLabel(value)),
            // A time axis places a label per tick and will happily draw them on
            // top of each other. The category axis already drops the ones that
            // collide, and the renderer this replaced reserved a minimum gap;
            // without this a few months of daily points is an unreadable smear.
            hideOverlap: true,
            rotate: rotation,
          },
          axisLine: { lineStyle: { color: theme.axis } },
          axisTick: { show: false },
          // Recharts' CartesianGrid drew vertical={false}: only the y axis
          // carried gridlines. A time axis draws its own split lines by
          // default, so they need suppressing explicitly here.
          splitLine: { show: false },
        }
      : {
          type: 'category',
          data: categories,
          boundaryGap: series.some((entry) => entry.type === 'bar'),
          ...(xTitle
            ? {
                name: xTitle,
                nameLocation: 'middle',
                nameGap: rotation ? 56 : 28,
                nameTextStyle: axisTitleStyle,
              }
            : {}),
          axisLabel: {
            color: theme.textMuted,
            fontFamily: theme.fontFamily,
            fontSize,
            // Ranked bar comparisons are capped at ten categories, so every
            // category can and should be named. ECharts' automatic interval
            // otherwise hides alternate labels even though their bars remain
            // visible, leaving the reader to guess what half the marks mean.
            interval: showEveryCategoryTick ? 0 : 'auto',
            rotate: rotation,
            hideOverlap: !showEveryCategoryTick,
            ...(wrapCategoryLabels
              ? { formatter: wrapCategoryLabel, lineHeight: fontSize + 3, align: 'center', verticalAlign: 'top' }
              : {}),
            // A long category name is otherwise drawn at full length and
            // clipped by the card's own edge, which cuts it mid-word with
            // nothing to say it was cut. Truncated here instead, so the label
            // ends in an ellipsis and the tooltip carries the whole name.
            width: CATEGORY_LABEL_WIDTH,
            overflow: 'truncate',
            ellipsis: '…',
          },
          axisLine: { lineStyle: { color: theme.axis } },
          axisTick: { show: false },
          // Recharts' CartesianGrid drew vertical={false}: only the y axis
          // carried gridlines.
          splitLine: { show: false },
        },
    yAxis,
    series,
  };
  // A saved orientation wins; only an unset default adapts to a narrow, dense card.
  if (horizontal) {
    const width = bodyWidth ?? 480;
    const categoryLabelWidth = Math.max(
      fontSize,
      Math.min(
        width * CATEGORY_LABEL_SHARE,
        width - AXIS_OUTER_INSET - AXIS_LABEL_GAP - HORIZONTAL_PLOT_RIGHT - MIN_HORIZONTAL_PLOT_WIDTH,
      ),
    );
    const measure = axisTextMeasure(fontSize, theme.fontFamily);
    // ECharts' category text box has this explicit width even when its words
    // are shorter. Measure ticks against the plot left after that box fits.
    const plotWidth = Math.max(
      1,
      width - AXIS_OUTER_INSET - AXIS_LABEL_GAP - categoryLabelWidth - HORIZONTAL_PLOT_RIGHT,
    );
    const formatTick = (value: unknown) => horizontalAxisLabel(primaryFormat, value);
    const tickValues = primaryBound.values ?? [
      primaryBound.min ?? 0,
      primaryBound.max ?? 0,
      ...rows.flatMap((row) => primarySeries.map((name) => Number(row[name]))).filter(Number.isFinite),
    ];
    const tickWidth = Math.max(fontSize, ...tickValues.map((value) => measure(formatTick(value))));
    const intervals = Math.max(1, Math.min(AXIS_INTERVALS, Math.floor(plotWidth / (tickWidth + AXIS_LABEL_GAP))));
    // width is a truncation ceiling, not a reserved gutter: the renderer
    // measures the resulting text, so Phone needs less space than a vendor name.
    option.grid = {
      left: AXIS_OUTER_INSET,
      right: HORIZONTAL_PLOT_RIGHT,
      top: 16,
      bottom: legendInset,
      outerBoundsMode: 'same',
      outerBoundsContain: 'all',
    };
    option.graphic = rankingGraphic ? [rankingGraphic] : [];
    option.xAxis = {
      ...yAxis[0],
      splitNumber: intervals,
      // Changing splitNumber alone cannot thin an explicitly derived interval.
      // Keep the original floor/ceiling while retaining a familiar tick ladder.
      interval:
        primaryBound.interval === undefined ? undefined : primaryBound.interval * Math.ceil(AXIS_INTERVALS / intervals),
      axisLabel: {
        ...(yAxis[0].axisLabel as Record<string, unknown>),
        formatter: formatTick,
        hideOverlap: true,
      },
    };
    option.yAxis = {
      type: 'category',
      data: categories,
      inverse: true,
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize,
        interval: 0,
        width: categoryLabelWidth,
        overflow: 'truncate',
      },
      axisLine: { show: false },
      axisTick: { show: false },
    };
    option.dataZoom = [];
  }
  return option;
}

/** Card types this builder draws. Anything else still needs its own branch. */
export const LENS_ECHARTS_SUPPORTED_TYPES: ReadonlyArray<LensCardType> = [
  'bar',
  'stacked',
  'line',
  'area',
  'combo',
  'pie',
  'scatter',
  'heatmap',
  'boxplot',
];

export type LensEChartsPieInput = {
  /** Shape returned by buildPieModel in ./cardView: { slices: Array<{ name: string; value: number; share: number }> } */
  pie: { slices: Array<{ name: string; value: number; share: number }> };
  theme: LensEChartsTheme;
  config?: LensCardConfig;
};

/**
 * A pie has no axes, so the series is a single data array of named slices
 * with their own colour rather than the shared series/axis shape above.
 */
export function buildLensEChartsPieOption({ pie, theme, config }: LensEChartsPieInput): LensEChartsOption {
  return {
    animation: false,
    textStyle: { fontFamily: theme.fontFamily, color: theme.text },
    legend: {
      show: true,
      bottom: 0,
      icon: 'roundRect',
      itemHeight: 8,
      itemWidth: 12,
      textStyle: { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize: LEGEND_FONT_SIZE },
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: theme.surface,
      borderColor: theme.axis,
      textStyle: { color: theme.text, fontFamily: theme.fontFamily, fontSize: 12 },
      formatter: (params: unknown) => {
        const point = params as { name?: string; value?: unknown; dataIndex?: number };
        const slice = pie.slices[point.dataIndex ?? -1];
        const value = formatLensValue(point.value, config?.format);
        // share is already a 0-1 fraction, so percentScale must not divide it
        // by 100 a second time the way a raw SQL percentage column would need.
        const share = formatLensValue(slice?.share, { kind: 'percent', percentScale: 'unit', decimals: 0 });
        return `${point.name ?? ''}: <b>${value}</b> (${share})`;
      },
    },
    series: [
      {
        type: 'pie',
        radius: '70%',
        animation: false,
        label: { show: false },
        itemStyle: { borderColor: theme.surface, borderWidth: 2 },
        data: pie.slices.map((slice, index) => ({
          name: slice.name,
          value: slice.value,
          itemStyle: { color: theme.palette[index % theme.palette.length] },
        })),
      },
    ],
  };
}

export type LensEChartsScatterInput = {
  scatter: {
    xKey: string;
    yKey: string;
    groups: Array<{ name: string; rows: Array<Record<string, unknown>> }>;
  };
  theme: LensEChartsTheme;
  config?: LensCardConfig;
};

/**
 * A scatter plots two measures against each other rather than a measure
 * against a category, so both axes are value axes and neither is forced
 * through zero: a cluster sitting well away from the origin should fill the
 * plot, not hug one corner of it.
 */
export function buildLensEChartsScatterOption({ scatter, theme, config }: LensEChartsScatterInput): LensEChartsOption {
  const showLegend = scatter.groups.length > 1;
  const xFormat = fieldFormat(config, scatter.xKey) ?? config?.format;
  const yFormat = fieldFormat(config, scatter.yKey) ?? config?.format;
  const xLabel = seriesLabel(scatter.xKey, config);
  const yLabel = seriesLabel(scatter.yKey, config);

  return {
    animation: false,
    textStyle: { fontFamily: theme.fontFamily, color: theme.text },
    grid: { left: Y_AXIS_WIDTH, right: 16, top: 12, bottom: showLegend ? 28 : 8, containLabel: false },
    legend: showLegend
      ? {
          show: true,
          bottom: 0,
          icon: 'roundRect',
          itemHeight: 8,
          itemWidth: 12,
          textStyle: { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize: LEGEND_FONT_SIZE },
        }
      : { show: false },
    tooltip: {
      trigger: 'item',
      backgroundColor: theme.surface,
      borderColor: theme.axis,
      textStyle: { color: theme.text, fontFamily: theme.fontFamily, fontSize: 12 },
      formatter: (params: unknown) => {
        const point = params as { value?: [number, number] };
        const [x, y] = point.value ?? [];
        return [
          `${xLabel}: <b>${formatLensValue(x, xFormat)}</b>`,
          `${yLabel}: <b>${formatLensValue(y, yFormat)}</b>`,
        ].join('<br/>');
      },
    },
    xAxis: {
      type: 'value',
      scale: true,
      name: xLabel,
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize: AXIS_FONT_SIZE,
        formatter: (value: unknown) => compactAxisLabel(xFormat, value),
      },
      axisLine: { lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      // [3, 3] to match Recharts' CartesianGrid strokeDasharray="3 3" exactly.
      // Unlike the axis-chart grid, a scatter's CartesianGrid never set
      // vertical={false}, so both axes keep their split lines.
      splitLine: { lineStyle: { color: theme.grid, type: [3, 3] } },
    },
    yAxis: {
      type: 'value',
      scale: true,
      name: yLabel,
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize: AXIS_FONT_SIZE,
        formatter: (value: unknown) => compactAxisLabel(yFormat, value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: theme.grid, type: [3, 3] } },
    },
    series: scatter.groups.map((group, index) => ({
      name: group.name,
      type: 'scatter',
      symbolSize: 8,
      animation: false,
      itemStyle: { color: theme.palette[index % theme.palette.length] },
      data: group.rows
        .map((row) => [Number(row[scatter.xKey]), Number(row[scatter.yKey])])
        .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y)),
    })),
  };
}

export type LensEChartsHeatmapInput = {
  matrix: LensMatrixModel;
  theme: LensEChartsTheme;
  /** Sequential ramp for cell colour, light end first. */
  ramp: ReadonlyArray<string>;
  valueTitle?: string;
  /** The card's value format. A currency or percent metric must read the
   *  same in a heatmap cell as it does on every other card. */
  format?: LensValueFormat;
  /**
   * The card's x-axis settings. A heatmap offers the same axis title, label
   * angle and label density controls as an axis chart, and a control the
   * renderer ignores is a control that lies about what it does.
   */
  xAxis?: LensCardConfig['xAxis'];
};

/**
 * ECharts draws a heatmap as a first-class series, with visualMap owning both
 * the colour ramp and the legend. The cell data is [xIndex, yIndex, value]
 * triples against two category axes.
 */
export function buildLensEChartsHeatmapOption({
  matrix,
  theme,
  ramp,
  valueTitle = 'Value',
  format,
  xAxis,
}: LensEChartsHeatmapInput): LensEChartsOption {
  const xIndex = new Map(matrix.xValues.map((value, index) => [value, index]));
  const yIndex = new Map(matrix.yValues.map((value, index) => [value, index]));
  const rotation = xAxis?.tickRotation ?? 0;
  const allTicks = xAxis?.tickDensity === 'all';
  const xTitle = xAxis?.title?.trim() || undefined;
  // A rotated or forced-on label needs the room it takes, and an axis title
  // needs a line of its own beneath the colour legend.
  const bottom = 56 + (rotation ? 18 : 0) + (xTitle ? 20 : 0);

  return {
    animation: false,
    textStyle: { fontFamily: theme.fontFamily, color: theme.text },
    grid: { left: 16, right: 16, top: 12, bottom, containLabel: true },
    tooltip: {
      backgroundColor: theme.surface,
      borderColor: theme.axis,
      textStyle: { color: theme.text, fontFamily: theme.fontFamily, fontSize: 12 },
      formatter: (params: unknown) => {
        const cell = (params as { value?: [number, number, number] }).value;
        if (!cell) {
          return '';
        }
        return `${matrix.yValues[cell[1]]}, ${matrix.xValues[cell[0]]}<br/>${valueTitle}: <b>${formatLensValue(cell[2], format)}</b>`;
      },
    },
    xAxis: {
      type: 'category',
      data: matrix.xValues,
      splitArea: { show: false },
      ...(xTitle ? { name: xTitle, nameLocation: 'middle', nameGap: 34 } : {}),
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize: AXIS_FONT_SIZE,
        hideOverlap: !allTicks,
        ...(allTicks ? { interval: 0 } : {}),
        ...(rotation ? { rotate: rotation } : {}),
      },
      axisLine: { lineStyle: { color: theme.axis } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: matrix.yValues,
      // A category axis originates bottom-left, which prints the first row last.
      // Inverting keeps row order the same as the model and the other renderers.
      inverse: true,
      splitArea: { show: false },
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize: AXIS_FONT_SIZE,
        width: 180,
        overflow: 'truncate',
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    visualMap: {
      min: matrix.min,
      max: matrix.max,
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      itemWidth: 12,
      itemHeight: 120,
      text: [formatLensValue(matrix.max, format), formatLensValue(matrix.min, format)],
      textStyle: { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize: 11 },
      inRange: { color: [...ramp] },
    },
    series: [
      {
        type: 'heatmap',
        data: matrix.cells
          .filter((cell) => cell.value !== null)
          .map((cell) => [xIndex.get(cell.x) ?? 0, yIndex.get(cell.y) ?? 0, cell.value]),
        // A hairline in the card surface separates cells without a border that
        // reads as a grid line at small cell sizes.
        itemStyle: { borderColor: theme.surface, borderWidth: 1 },
        emphasis: { itemStyle: { borderColor: theme.text, borderWidth: 1 } },
        progressive: 2000,
      },
    ],
  };
}

export type LensEChartsBoxplotInput = {
  distributions: Array<LensDistribution>;
  theme: LensEChartsTheme;
  valueFormat?: LensValueFormat;
  /** Shared upper bound, so every renderer draws the distribution at one scale. */
  max?: number;
};

/**
 * ECharts has a boxplot series but it takes the five statistics already
 * computed, in [min, q1, median, q3, max] order. Outliers are a separate
 * scatter series drawn against the same axes.
 */
export function buildLensEChartsBoxplotOption({
  distributions,
  theme,
  valueFormat,
  max,
}: LensEChartsBoxplotInput): LensEChartsOption {
  const colour = theme.palette[0];
  const outlierColour = theme.palette[1];

  return {
    animation: false,
    textStyle: { fontFamily: theme.fontFamily, color: theme.text },
    grid: { left: 64, right: 16, top: 12, bottom: 24, containLabel: false },
    tooltip: {
      trigger: 'item',
      backgroundColor: theme.surface,
      borderColor: theme.axis,
      textStyle: { color: theme.text, fontFamily: theme.fontFamily, fontSize: 12 },
      formatter: (params: unknown) => {
        const point = params as { seriesIndex?: number; dataIndex?: number; value?: unknown };
        // The outlier series is index 1 and carries its own [categoryIndex,
        // value] pair, so the category comes from the point's own index
        // rather than dataIndex, which counts outliers, not categories.
        if (point.seriesIndex === 1) {
          const [categoryIndex, value] = (point.value as [number, number]) ?? [];
          const category = distributions[categoryIndex]?.category ?? '';
          return `${category}<br/>Outlier: <b>${formatLensValue(value, valueFormat)}</b>`;
        }
        const entry = distributions[point.dataIndex ?? -1];
        if (!entry) {
          return '';
        }
        const line = (label: string, value: number) => `${label}: <b>${formatLensValue(value, valueFormat)}</b>`;
        return [
          entry.category,
          line('Max', entry.max),
          line('Q3', entry.q3),
          line('Median', entry.median),
          line('Q1', entry.q1),
          line('Min', entry.min),
        ].join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: distributions.map((entry) => entry.category),
      axisLabel: { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize: AXIS_FONT_SIZE, hideOverlap: true },
      axisLine: { lineStyle: { color: theme.axis } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max,
      axisLabel: {
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize: AXIS_FONT_SIZE,
        formatter: (value: unknown) => compactAxisLabel(valueFormat, value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: theme.grid, type: 'dashed' } },
    },
    series: [
      {
        type: 'boxplot',
        data: distributions.map((entry) => [entry.min, entry.q1, entry.median, entry.q3, entry.max]),
        itemStyle: { color: colour, borderColor: colour, opacity: 0.85 },
        boxWidth: [10, 34],
      },
      {
        type: 'scatter',
        symbolSize: 5,
        itemStyle: { color: outlierColour },
        data: distributions.flatMap((entry, index) => entry.outliers.map((value) => [index, value])),
      },
    ],
  };
}

/** True when the row count is past the point a per-row SVG mark stops being viable. */
export function exceedsSvgMarkCeiling(rowCount: number): boolean {
  return rowCount > LENS_LAYOUT.chartMarkCeiling;
}
